
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TestResponse } from "@/types/test-response";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { IntegrityDisplay } from "../responses-table/IntegrityDisplay";
import { format } from "date-fns";

interface AdjacentResponseNavigationProps {
  previousResponse: TestResponse | null;
  nextResponse: TestResponse | null;
  onNavigate: (responseId: string) => void;
}

export function AdjacentResponseNavigation({
  previousResponse,
  nextResponse,
  onNavigate
}: AdjacentResponseNavigationProps) {
  if (!previousResponse && !nextResponse) return null;
  
  // Helper function to render recruitment status badge
  const renderStatusBadge = (status: string | null) => {
    if (!status) return null;
    
    const statusConfig: Record<string, { variant: "default" | "secondary" | "destructive" | "outline", label: string }> = {
      "pending": { variant: "outline", label: "Pending" },
      "shortlisted": { variant: "default", label: "Shortlisted" },
      "hired": { variant: "default", label: "Hired" },
      "rejected": { variant: "destructive", label: "Rejected" }
    };
    
    const config = statusConfig[status] || { variant: "outline", label: status };
    
    return (
      <Badge variant={config.variant} className="text-xs font-normal">
        {config.label}
      </Badge>
    );
  };

  // Helper function to calculate AI score percentage
  const calculateAIScore = (response: TestResponse | null) => {
    if (!response) return 0;
    const totalMarks = response.test.questions?.reduce((sum, q) => sum + (q.marks || 1), 0) || 0;
    return totalMarks > 0 ? Math.round(((response.total_ai_score || 0) / totalMarks) * 100) : 0;
  };

  // Helper function to render the response card
  const renderResponseCard = (response: TestResponse | null, isNext: boolean) => {
    if (!response) return null;
    
    const aiScore = calculateAIScore(response);
    const submissionDate = response.completed_at 
      ? format(new Date(response.completed_at), "dd MMM yyyy")
      : format(new Date(response.created_at), "dd MMM yyyy");
    
    // Get initials for avatar fallback - handle missing name
    const fullName = response.candidate?.full_name || "Unknown";
    const initials = fullName
      .split(' ')
      .map(name => name[0] || '')
      .join('')
      .toUpperCase()
      .substring(0, 2) || "??";
    
    return (
      <Card 
        className="relative overflow-hidden group border transition-all hover:border-border/80 hover:shadow-md cursor-pointer"
        onClick={() => onNavigate(response.id)}
      >
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              {isNext ? (
                <>
                  <p className="text-sm text-muted-foreground">Next Response</p>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </>
              ) : (
                <>
                  <ChevronLeft className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Previous Response</p>
                </>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {submissionDate}
            </div>
          </div>
          
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8">
                <AvatarImage src={response.candidate?.avatar_url || ''} alt={response.candidate?.full_name || 'Candidate'} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div>
                <h4 className="font-medium line-clamp-1">{response.candidate?.full_name || 'Unnamed Candidate'}</h4>
                <p className="text-xs text-muted-foreground line-clamp-1">{response.candidate?.email || 'No email'}</p>
              </div>
            </div>
            
            <div className="flex flex-col items-end gap-2">
              {renderStatusBadge(response.recruitment_status || null)}
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-3">
            <div>
              <span className="text-xs text-muted-foreground block mb-1">AI Score</span>
              <span className="font-medium">{aiScore}%</span>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-xs text-muted-foreground block mb-1">Integrity</span>
              <IntegrityDisplay score={response.integrity_score || 0} />
            </div>
          </div>
          
          {/* Hover overlay with navigation button */}
          <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Button variant="default" size="sm" className="shadow-md">
              {isNext ? (
                <>
                  <span>Next</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              ) : (
                <>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  <span>Previous</span>
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Previous Response */}
      {previousResponse && renderResponseCard(previousResponse, false)}
      
      {/* Next Response */}
      {nextResponse && renderResponseCard(nextResponse, true)}
    </div>
  );
}
